import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeQrFromImageFile, decodeQrFromImageData, parseAnyOtpQr, parseOtpFileText, otpDedupeKey } from '../hooks/useOtpEntries';
import { Input, PillButton, IconCircle, Modal, SegmentedControl } from '../ui';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as ImageIcon } from '../assets/icons/image.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/check.svg';
import { ReactComponent as FileImportIcon } from '../assets/icons/file-import.svg';
import './OtpAddModal.css';

const MAX_VIDEO_SIDE = 1280; // un QR de exportación de Google Authenticator es denso: no reducir más
const SCAN_INTERVAL_MS = 150;
const REPEAT_IGNORE_MS = 3000; // el mismo QR frente a la cámara no se reprocesa en cada fotograma

/** Cámara con escaneo continuo. Llama a onText(texto) por cada QR leído y a onError(código) si no hay cámara. */
function CameraScanner({ onText, onError }) {
  const videoRef = useRef(null);
  const onTextRef = useRef(onText);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTextRef.current = onText; onErrorRef.current = onError; });

  useEffect(() => {
    let stopped = false;
    let stream = null;
    const video = videoRef.current;
    let raf = 0;
    let last = 0;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { onErrorRef.current('unsupported'); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (err) {
        const name = err?.name;
        onErrorRef.current(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied'
          : name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError' ? 'none' : 'generic');
        return;
      }
      if (stopped) { stream.getTracks().forEach((tr) => tr.stop()); return; }

      video.srcObject = stream;
      await video.play().catch(() => {});
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      const tick = (now) => {
        if (stopped) return;
        raf = requestAnimationFrame(tick);
        if (now - last < SCAN_INTERVAL_MS || video.readyState < 2 || !video.videoWidth) return;
        last = now;
        const scale = Math.min(1, MAX_VIDEO_SIDE / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const text = decodeQrFromImageData(ctx.getImageData(0, 0, canvas.width, canvas.height), 'dontInvert');
        if (text) onTextRef.current(text);
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((tr) => tr.stop());
      video.srcObject = null;
    };
  }, []);

  return <video ref={videoRef} className="sgc-otp-cam__video" playsInline muted autoPlay />;
}

/**
 * Modal de alta de cuentas 2FA: manual, imágenes (varias), cámara (continua) y fichero.
 * addEntries(list) → { added, skipped, blocked }; flush() → Promise<boolean> (ver useOtpEntries).
 */
function OtpAddModal({ open, onClose, entries, addEntries, flush }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('manual');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // manual
  const [issuer, setIssuer] = useState('');
  const [account, setAccount] = useState('');
  const [secret, setSecret] = useState('');
  // imágenes
  const [dragOver, setDragOver] = useState(false);
  const [imagesResult, setImagesResult] = useState(null);
  const imagesInputRef = useRef(null);
  // cámara
  const [cam, setCam] = useState({ count: 0, msg: '', tone: 'info', failure: '' });
  const lastScanRef = useRef({ text: '', time: 0 });
  // fichero
  const [fileText, setFileText] = useState('');
  const [unchecked, setUnchecked] = useState(() => new Set());
  const [fileResult, setFileResult] = useState(null);
  const fileInputRef = useRef(null);

  const blockedMessage = useCallback((kind) => (kind === 'decrypt' ? t('otp.undecryptableTitle') : t('otp.loadFailed')), [t]);

  const reset = () => {
    setMode('manual'); setError(''); setBusy(false);
    setIssuer(''); setAccount(''); setSecret('');
    setImagesResult(null); setDragOver(false);
    setCam({ count: 0, msg: '', tone: 'info', failure: '' }); lastScanRef.current = { text: '', time: 0 };
    setFileText(''); setUnchecked(new Set()); setFileResult(null);
  };

  const close = async () => {
    if (busy) return;
    if (!(await flush())) { setError(t('otp.saveFailed')); return; }
    reset();
    onClose();
  };

  const changeMode = (m) => { setError(''); setMode(m); };

  // ---- manual
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const raw = secret.trim();
    if (!raw) { setError(t('otp.secretRequired')); return; }
    const [fromUri] = raw.startsWith('otpauth://') ? parseAnyOtpQr(raw) : [];
    if (raw.startsWith('otpauth://') && !fromUri) { setError(t('otp.invalidQR')); return; }
    const r = addEntries([fromUri || { type: 'totp', issuer, account, secret: raw, digits: 6, period: 30, algorithm: 'SHA1' }]);
    if (r.blocked) { setError(blockedMessage(r.blocked)); return; }
    if (!r.added) { setError(r.skipped ? t('otp.duplicate') : t('otp.invalidQR')); return; }
    await close();
  };

  // ---- imágenes
  const processImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true); setError(''); setImagesResult(null);
    const found = [];
    const failed = [];
    for (const f of files) { // en serie: una foto de 12 Mpx ya pesa bastante en memoria
      try {
        const list = parseAnyOtpQr(await decodeQrFromImageFile(f));
        if (list.length) found.push(...list); else failed.push(f.name);
      } catch {
        failed.push(f.name);
      }
    }
    const r = addEntries(found);
    if (r.blocked) { setError(blockedMessage(r.blocked)); setBusy(false); return; }
    const saved = r.added ? await flush() : true;
    setImagesResult({ files: files.length, added: r.added, skipped: r.skipped, failed, saved });
    setBusy(false);
  };

  // ---- cámara
  const onCameraText = useCallback((text) => {
    const now = Date.now();
    const last = lastScanRef.current;
    if (text === last.text && now - last.time < REPEAT_IGNORE_MS) return;
    lastScanRef.current = { text, time: now };

    const list = parseAnyOtpQr(text);
    if (!list.length) { setCam((c) => ({ ...c, msg: t('otp.cameraNotOtp'), tone: 'warn' })); return; }
    const r = addEntries(list);
    if (r.blocked) { setCam((c) => ({ ...c, msg: blockedMessage(r.blocked), tone: 'warn' })); return; }
    if (!r.added) { setCam((c) => ({ ...c, msg: t('otp.duplicate'), tone: 'warn' })); return; }
    if (navigator.vibrate) navigator.vibrate(60);
    setCam((c) => ({
      ...c,
      count: c.count + r.added,
      tone: 'ok',
      msg: list.length === 1 ? t('otp.cameraAdded', { name: list[0].issuer || list[0].account || '—' }) : t('otp.cameraAddedMany', { count: r.added }),
    }));
  }, [addEntries, blockedMessage, t]);

  const onCameraError = useCallback((kind) => setCam((c) => ({ ...c, failure: kind })), []);
  const cameraFailureText = { denied: t('otp.cameraDenied'), none: t('otp.cameraNone'), unsupported: t('otp.cameraUnsupported'), generic: t('otp.cameraError') };

  // ---- fichero
  const parsedFile = useMemo(() => (fileText.trim() ? parseOtpFileText(fileText) : null), [fileText]);
  const existingKeys = useMemo(() => new Set(entries.map(otpDedupeKey)), [entries]);
  const preview = useMemo(() => {
    const seen = new Set(existingKeys);
    return (parsedFile?.entries || []).map((e) => {
      const key = otpDedupeKey(e);
      const duplicate = seen.has(key);
      seen.add(key);
      return { entry: e, duplicate };
    });
  }, [parsedFile, existingKeys]);
  const selected = preview.filter((p, i) => !p.duplicate && !unchecked.has(i));

  const loadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUnchecked(new Set()); setFileResult(null);
    setFileText(await file.text());
  };

  const toggle = (i) => setUnchecked((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const addSelected = async () => {
    setBusy(true); setError('');
    const r = addEntries(selected.map((p) => p.entry));
    if (r.blocked) { setError(blockedMessage(r.blocked)); setBusy(false); return; }
    const saved = await flush();
    setFileResult({ added: r.added, skipped: r.skipped, saved });
    setBusy(false);
  };

  const fileError = parsedFile?.error === 'encrypted' ? t('otp.fileEncrypted')
    : parsedFile?.error === 'unknown' ? t('otp.fileUnknown')
      : parsedFile && !parsedFile.entries.length ? t('otp.importEmpty') : '';

  return (
    <Modal open={open} onClose={close} title={t('otp.addEntry')} className="sgc-otp-add">
      <SegmentedControl
        className="sgc-2fa__add-modes"
        value={mode}
        onChange={changeMode}
        options={[
          { value: 'manual', label: t('otp.modeManual') },
          { value: 'images', label: t('otp.modeImages') },
          { value: 'camera', label: t('otp.modeCamera') },
          { value: 'file', label: t('otp.modeFile') },
        ]}
      />

      {mode === 'manual' && (
        <form className="sgc-2fa__add-form" onSubmit={handleManualSubmit}>
          <Input icon={ShieldIcon} label={t('otp.issuer')} value={issuer} onChange={(e) => setIssuer(e.target.value)} />
          <Input label={t('otp.account')} value={account} onChange={(e) => setAccount(e.target.value)} />
          <Input
            icon={KeyIcon}
            label={t('otp.secret')}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            error={error}
            placeholder={t('otp.secretPlaceholder')}
          />
          <p className="sgc-2fa__help">{t('otp.secretHelp')}</p>
          <PillButton type="submit">{t('otp.addEntry')}</PillButton>
        </form>
      )}

      {mode === 'images' && (
        <div className="sgc-2fa__add-form">
          <button
            type="button"
            className={`sgc-2fa__upload-zone ${dragOver ? 'sgc-2fa__upload-zone--over' : ''}`}
            disabled={busy}
            onClick={() => imagesInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); processImages(e.dataTransfer.files); }}
          >
            <IconCircle icon={ImageIcon} tone="accent" size={48} />
            <span>{busy ? t('common.loading') : t('otp.imagesDrop')}</span>
          </button>
          <input ref={imagesInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { processImages(e.target.files); e.target.value = ''; }} />
          {error && <p className="sgc-input__error">{error}</p>}
          {imagesResult && (
            <div className="sgc-otp-add__result" role="status">
              <p>{t('otp.imagesAdded', { added: imagesResult.added, files: imagesResult.files })}</p>
              {imagesResult.skipped > 0 && <p>{t('otp.skippedDuplicates', { count: imagesResult.skipped })}</p>}
              {imagesResult.failed.length > 0 && <p className="sgc-otp-add__warn">{t('otp.imagesNoQR', { files: imagesResult.failed.join(', ') })}</p>}
              {!imagesResult.saved && <p className="sgc-otp-add__warn">{t('otp.saveFailed')}</p>}
            </div>
          )}
          <PillButton variant="secondary" onClick={close} disabled={busy}>{t('otp.done')}</PillButton>
        </div>
      )}

      {mode === 'camera' && (
        <div className="sgc-2fa__add-form">
          {cam.failure ? (
            <p className="sgc-input__error" role="alert">{cameraFailureText[cam.failure]}</p>
          ) : (
            <>
              <div className="sgc-otp-cam">
                <CameraScanner onText={onCameraText} onError={onCameraError} />
                <span className="sgc-otp-cam__counter">{t('otp.addedCounter', { count: cam.count })}</span>
              </div>
              <p className={`sgc-otp-cam__status sgc-otp-cam__status--${cam.tone}`} role="status" aria-live="polite">
                {cam.msg || t('otp.cameraHelp')}
              </p>
            </>
          )}
          <PillButton onClick={close} icon={CheckIcon}>{t('otp.done')}</PillButton>
          {error && <p className="sgc-input__error">{error}</p>}
        </div>
      )}

      {mode === 'file' && (
        <div className="sgc-2fa__add-form">
          <textarea
            className="sgc-otp-add__textarea"
            rows={4}
            value={fileText}
            placeholder={t('otp.filePaste')}
            onChange={(e) => { setFileText(e.target.value); setUnchecked(new Set()); setFileResult(null); }}
            spellCheck={false}
          />
          <input ref={fileInputRef} type="file" accept=".txt,.json,.2fas,.csv,text/plain,application/json" hidden onChange={loadFile} />
          <PillButton variant="secondary" icon={FileImportIcon} onClick={() => fileInputRef.current?.click()}>{t('otp.fileChoose')}</PillButton>

          {fileError && <p className="sgc-input__error" role="alert">{fileError}</p>}
          {error && <p className="sgc-input__error">{error}</p>}
          {parsedFile?.skipped > 0 && !fileError && <p className="sgc-2fa__help">{t('otp.fileSkipped', { count: parsedFile.skipped })}</p>}

          {preview.length > 0 && !fileResult && (
            <ul className="sgc-otp-add__list">
              {preview.map((p, i) => (
                <li key={i}>
                  <label className="sgc-otp-add__item">
                    <input type="checkbox" checked={!p.duplicate && !unchecked.has(i)} disabled={p.duplicate} onChange={() => toggle(i)} />
                    <span className="sgc-otp-add__item-name">{p.entry.issuer || t('otp.unknownIssuer')}</span>
                    <span className="sgc-otp-add__item-account">{p.entry.account}</span>
                    {p.duplicate && <span className="sgc-otp-add__badge">{t('otp.duplicate')}</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}

          {fileResult && (
            <div className="sgc-otp-add__result" role="status">
              <p>{t('otp.fileAdded', { added: fileResult.added, skipped: fileResult.skipped })}</p>
              {!fileResult.saved && <p className="sgc-otp-add__warn">{t('otp.saveFailed')}</p>}
            </div>
          )}

          {fileResult ? (
            <PillButton onClick={close}>{t('otp.done')}</PillButton>
          ) : (
            <PillButton onClick={addSelected} disabled={!selected.length || busy} loading={busy}>
              {t('otp.addSelected', { count: selected.length })}
            </PillButton>
          )}
        </div>
      )}
    </Modal>
  );
}

export default OtpAddModal;
